import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Input,
  InputLabel,
} from '@mui/material'
import moment from 'moment'
import React from 'react'
import Select from '../../../common/select/Select'
import { getMonthName } from '../../../common/utils'
import { i18n } from '../../index'
import {
  IncentiveSalesCorrectionFactorInput,
  IncentiveSalesCorrectionFactorResponse,
} from './incentiveSalesCorrectionFactorTypes'

interface Props {
  isOpen: boolean
  onClose: () => void
  onSave: (inputData: IncentiveSalesCorrectionFactorInput[]) => void
  existingFactors: IncentiveSalesCorrectionFactorResponse[]
}

const monthNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

interface YearOption {
  label: number
  value: number
}

interface IncentiveSalesCorrectionFactorFormData {
  id?: string
  month: number
  factor: string
}

export default function IncentiveSalesCorrectionFactorDialog({ isOpen, onClose, onSave, existingFactors }: Props) {
  const [selectedYear, setSelectedYear] = React.useState<number | undefined>()
  const [factorSelections, setFactorSelections] = React.useState<IncentiveSalesCorrectionFactorFormData[]>([])
  const thisYear = moment().year()
  const nextYear = moment().year() + 1
  const selectableYears: YearOption[] = [
    { label: thisYear, value: thisYear },
    { label: nextYear, value: nextYear },
  ]

  function onYearSelected(year: YearOption) {
    const existingFactorsForYear = existingFactors.find((factorResponse) => factorResponse.year === year.value)
    if (existingFactorsForYear) {
      const newFactorSelections = existingFactorsForYear.factors.map((factor) => ({
        id: factor.id,
        month: factor.month,
        factor: '' + factor.factor,
      }))
      setFactorSelections(newFactorSelections)
    } else {
      setFactorSelections(
        monthNumbers.map((monthNumber) => ({
          month: monthNumber,
          factor: '1',
        })),
      )
    }
    setSelectedYear(year.value)
  }

  function selectBoxGetValueOverride(value: YearOption, options: ReadonlyArray<YearOption>) {
    options.find((option) => option.value === value.value)
  }

  function onFactorChange(month: number, factor: string) {
    const newFactorSelections = factorSelections.map((selection) => {
      if (selection.month === month) {
        return { ...selection, factor: factor }
      }
      return selection
    })
    setFactorSelections(newFactorSelections)
  }

  function onSubmit() {
    if (selectedYear) {
      const inputData: IncentiveSalesCorrectionFactorInput[] = factorSelections.map((factorInput) => ({
        id: factorInput.id,
        year: selectedYear,
        month: factorInput.month,
        factor: parseFloat(factorInput.factor),
      }))
      reset()
      onSave(inputData)
    }
  }

  function getFactorValueByMonth(month: number) {
    const factor = factorSelections.find((selection) => selection.month === month)
    return factor ? factor.factor : 1
  }

  function reset() {
    setSelectedYear(undefined)
    setFactorSelections([])
  }

  function resetAndClose() {
    reset()
    onClose()
  }

  return (
    <Dialog open={isOpen} onClose={resetAndClose} keepMounted={false}>
      <DialogTitle>{i18n.t('admin.incentiveSalesTargets.correctionFactorDialogTitle')}</DialogTitle>
      <DialogContent>
        <Select
          label={i18n.t('common.year')}
          options={selectableYears}
          onChange={onYearSelected}
          value={`${selectedYear}`}
          getValueOverride={selectBoxGetValueOverride}
        />
        {monthNumbers.map((monthNumber) => (
          <FormControl key={monthNumber}>
            <InputLabel htmlFor={`factor-${monthNumber}`}>{getMonthName(monthNumber)}</InputLabel>
            <Input
              id={`factor-${monthNumber}`}
              disabled={!selectedYear}
              type='number'
              value={getFactorValueByMonth(monthNumber)}
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                onFactorChange(monthNumber, event.target.value)
              }
              inputProps={{
                'aria-label': getMonthName(monthNumber),
              }}
              data-test-id={`factor-${monthNumber}`}
            />
          </FormControl>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose} color='primary' variant='contained'>
          {i18n.t('buttons.close')}
        </Button>
        <Button
          data-testid='save-correction-factors'
          onClick={onSubmit}
          color='primary'
          variant='contained'
          disabled={selectedYear === undefined}
        >
          {i18n.t('buttons.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
